import { createSelector } from 'reselect';

import {
	GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment,
	GdsService_OrderAdditionalServiceGdsServiceVip_Fragment
} from '@websky/graphql';
import { GdsBaggageAndVipServiceType, GdsServicesLimitInfo } from '../../types';
import { CheckoutState } from './reducers';
import { getGdsServicesCalculatedCountInfoSelector } from '@shared/lib/gdsService/getGdsServicesCalculatedCountInfoSelector';

const getOrder = (state: CheckoutState) => state.order;

const getAdditionalServices = createSelector(getOrder, order => order.additionalServices);

const getGdsServices = createSelector(
	getAdditionalServices,
	additionalServices => additionalServices?.gdsServices?.services ?? []
);

const getServicesRestrictionsByType = createSelector(
	getOrder,
	order => order.additionalServices?.gdsServices?.servicesRestrictionsByType ?? []
);

const getSegments = createSelector(getOrder, order => order.flight?.segments ?? []);

const getSegmentIds = createSelector(getSegments, segments => segments?.map(segment => segment.segment.id) ?? []);

const getPassengerIds = (state: CheckoutState) => state.passengers.map(passenger => passenger.id);

const getSelectedServicesState = (state: CheckoutState) => state.selectedServices;

export const getGdsSelectedServicesCountInfo = createSelector(
	getGdsServices,
	getSelectedServicesState,
	(gdsServices, selectedServices) => {
		const gdsServiceMap = new Map(gdsServices.map(service => [service.id, service]));

		const result: GdsServicesLimitInfo = {};

		for (const passengerId in selectedServices) {
			const passengerServices = selectedServices[passengerId];
			for (const serviceId in passengerServices) {
				const services = passengerServices[serviceId];
				for (const selectedService of services) {
					const serviceInfo = gdsServiceMap?.get(selectedService?.servicePayload?.id);
					if (!serviceInfo) {
						console.warn(`Service info not found for ID: ${selectedService?.servicePayload?.id}`);
						continue;
					}

					const rfisc = serviceInfo.rfisc;
					const type = serviceInfo.type;
					let subType: GdsBaggageAndVipServiceType;
					const count: number = selectedService.number;

					if ((serviceInfo as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment).baggageType) {
						subType = (serviceInfo as GdsService_OrderAdditionalServiceGdsServiceBaggage_Fragment)
							.baggageType;
					}

					if ((serviceInfo as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType) {
						subType = (serviceInfo as GdsService_OrderAdditionalServiceGdsServiceVip_Fragment).subType;
					}

					// Initialize passenger entry if it doesn't exist
					if (!result[passengerId]) {
						result[passengerId] = {
							segments: {},
							total: { rfisc: {}, type: {}, subType: {} }
						};
					}

					// Update total rfisc count
					result[passengerId].total.rfisc[rfisc] = Number(result[passengerId].total.rfisc[rfisc] || 0) + count;

					// Update total type count
					result[passengerId].total.type[type] = Number(result[passengerId].total.type[type] || 0) + count;

					// Update total subType count if subType exists
					if (subType) {
						result[passengerId].total.subType[subType] =
							Number(result[passengerId].total.subType[subType] || 0) + count;
					}

					// Update segment-specific counts
					for (const segmentId of selectedService.segments) {
						if (!result[passengerId].segments[segmentId]) {
							result[passengerId].segments[segmentId] = {
								rfisc: {},
								type: {},
								subType: {}
							};
						}

						// Update segment rfisc count
						result[passengerId].segments[segmentId].rfisc[rfisc] =
							Number(result[passengerId].segments[segmentId].rfisc[rfisc] || 0) + count;

						// Update segment type count
						result[passengerId].segments[segmentId].type[type] =
							Number(result[passengerId].segments[segmentId].type[type] || 0) + count;

						// Update segment subType count if subType exists
						if (subType) {
							result[passengerId].segments[segmentId].subType[subType] =
								Number(result[passengerId].segments[segmentId].subType[subType] || 0) + count;
						}
					}
				}
			}
		}

		return result;
	}
);

export const getGdsServicesCalculatedCountInfo = createSelector(
	getPassengerIds,
	getSegmentIds,
	getServicesRestrictionsByType,
	getGdsSelectedServicesCountInfo,
	getGdsServicesCalculatedCountInfoSelector
);
